import { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';
import { BackendService } from './backend.js';
const unique = (values) => new Set(values).size === values.length;
const note = z.string().trim().min(1).max(500);
/** Selected facts, never a source upload, authorization grant or signed plan. */
export const SuitabilityFacts = z.object({
    schemaVersion: z.literal(1),
    projectKind: z.enum(['new', 'existing']),
    intent: z.enum(['full-app', 'backend-only']),
    frontend: z.enum(['static', 'server-rendered', 'none', 'unknown']),
    server: z.enum(['worker', 'process', 'none', 'unknown']),
    database: z.enum(['d1', 'postgres', 'retain-external', 'none', 'unknown']),
    /** These requirements refer to the application's database/server, not the platform internals. */
    requirements: z.array(z.enum([
        'postgres-wire-protocol', 'postgres-extensions', 'durable-jobs',
        'browser-automation', 'native-addons', 'persistent-disk',
        'persistent-process', 'long-running-compute', 'managed-embeddings',
        'unverified-dependency',
    ])).max(10).refine(unique, 'Requirements must be unique'),
    requiredServices: z.array(BackendService.shape.id).max(16).refine(unique, 'Services must be unique'),
    inspection: z.enum(['complete', 'partial', 'not-inspected']),
    evidence: z.array(note).min(1).max(32),
    unknowns: z.array(note).max(16),
}).strict();
/** Server-controlled release policy; it must not be accepted from a create request. */
export const SuitabilityPolicy = z.object({
    schemaVersion: z.literal(1),
    release: z.literal('v1'),
    revision: z.string().trim().min(1).max(64),
    allowedServices: z.array(BackendService.shape.id).max(16).refine(unique, 'Services must be unique'),
    managedAiReady: z.boolean(),
}).strict();
export const SuitabilityReason = z.object({
    code: z.enum([
        'postgres_required', 'external_database_connection_unverified',
        'server_runtime_unavailable', 'frontend_runtime_unavailable',
        'worker_requirement_unavailable', 'service_outside_release',
        'service_not_allowed', 'inspection_incomplete', 'unknown_requirement',
        'unverified_dependency', 'ai_budget_not_ready', 'deployment_target_missing',
    ]),
    severity: z.enum(['blocker', 'question']),
    message: note,
}).strict();
/** Eligibility over declared facts only, not proof of health, build, capacity or authority. */
export const SuitabilityAssessment = z.object({
    schemaVersion: z.literal(1),
    policyRevision: z.string().min(1).max(64),
    decision: z.enum(['supported', 'review-required', 'unsupported']),
    reasons: z.array(SuitabilityReason).max(64),
    scope: z.literal('declared-requirements'),
    artifactValidation: z.literal('not_checked'),
    authorization: z.literal('not_checked'),
    healthAndCapacity: z.literal('not_checked'),
    entitlements: z.literal('not_checked'),
}).strict();
/** A preview is never a stored provisioning plan or an authorization grant. */
export const SuitabilityPreview = z.object({
    schemaVersion: z.literal(1),
    assessment: SuitabilityAssessment,
    releasePolicy: SuitabilityPolicy,
    environment: z.object({
        name: z.string().min(1).max(32),
        provisioningConfigured: z.boolean(),
        liveHealth: z.literal('not_checked'),
        resourceAndSpendLimits: z.literal('not_checked'),
    }).strict(),
    provisioningGate: z.enum(['not_enforced', 'enforced']),
    note,
}).strict();
/** MCP advertises the same bounded contract REST validates. Refinements run on the server. */
export function suitabilityFactsJsonSchema() {
    return zodToJsonSchema(SuitabilityFacts, { target: 'jsonSchema7', $refStrategy: 'none' });
}
//# sourceMappingURL=suitability.js.map